import createClient from 'openapi-fetch'
import type { paths } from '../types/integrations/flow/operations'
import type {
  CompleteTaskRequest,
  RetryTaskRequest,
  StartFlowRequest,
  TriggerMessageTaskRequest,
} from '../types/integrations/flow/common'
import { flowQuerySerializer, getMiddleware } from './utils'
import type { FlowQuery, ProcessQuery, TaskQuery } from '../types/common'

const baseUrl = 'http://flow-process/api'
const FLOW_ID_BATCH_SIZE = 50

const flowClient = createClient<paths>({
  baseUrl,
})
flowClient.use(getMiddleware())

export async function createFlow(flowDefinitionId: string, body: StartFlowRequest, userHeader?: string) {
  const { data } = await flowClient.POST('/flow-definitions/{flowDefinitionId}', {
    params: { path: { flowDefinitionId }, header: { 'Flow-Process-User': userHeader } },
    body,
  })
  return data
}

export async function getFlows(query?: FlowQuery, userHeader?: string) {
  const { data } = await flowClient.GET('/flows', {
    params: { query, header: { 'Flow-Process-User': userHeader } },
    querySerializer: flowQuerySerializer,
  })
  return data
}

export async function getFlowById(flowId: string, query?: FlowQuery, userHeader?: string) {
  const { data } = await flowClient.GET('/flows/{flowId}', {
    params: { path: { flowId }, query, header: { 'Flow-Process-User': userHeader } },
  })

  return data
}

export async function archiveFlow(flowId: string, userHeader?: string) {
  return await flowClient.DELETE('/flows/{flowId}', {
    params: { path: { flowId }, header: { 'Flow-Process-User': userHeader } },
  })
}

export async function getFlowAttachment(flowId: string, attachmentKey: string) {
  const { data } = await flowClient.GET('/flows/{flowId}/attachments/{attachmentKey}', {
    params: { path: { flowId, attachmentKey } },
  })
  return data
}

export async function downloadFlowAttachment(flowId: string, attachmentKey: string) {
  const response = await fetch(`${baseUrl}/flows/${flowId}/attachments/${attachmentKey}/download`)

  const { ok, status, statusText } = response
  if (!ok) {
    const error = await response.text()
    throw new Error(`Failed to download flow attachment. Response: ${status} ${statusText} ${error}`)
  }

  return await response.arrayBuffer()
}

export async function getTasks(query?: TaskQuery, userHeader?: string) {
  const { data } = await flowClient.GET('/tasks', {
    params: { query, header: { 'Flow-Process-User': userHeader } },
    querySerializer: flowQuerySerializer,
  })
  return data
}

export type FlowTask = NonNullable<NonNullable<Awaited<ReturnType<typeof getTasks>>>['tasks']>[number]

export async function getTasksForFlows(flowIds: string[], query: TaskQuery, userHeader?: string): Promise<FlowTask[]> {
  const tasks: FlowTask[] = []
  for (let index = 0; index < flowIds.length; index += FLOW_ID_BATCH_SIZE) {
    const result = await getTasks({ ...query, flowId: flowIds.slice(index, index + FLOW_ID_BATCH_SIZE) }, userHeader)
    tasks.push(...(result?.tasks ?? []))
  }

  return tasks
}

export async function getTaskById(taskId: string, userHeader?: string) {
  const { data } = await flowClient.GET('/tasks/{taskId}', {
    params: { path: { taskId }, header: { 'Flow-Process-User': userHeader } },
  })
  return data
}

export async function completeTask(taskId: string, body: CompleteTaskRequest, userHeader?: string) {
  return await flowClient.POST('/tasks/{taskId}/complete', {
    params: { path: { taskId }, header: { 'Flow-Process-User': userHeader } },
    body,
  })
}

export async function triggerMessageTask(taskId: string, body: TriggerMessageTaskRequest, userHeader?: string) {
  return await flowClient.POST('/tasks/{taskId}/trigger', {
    params: { path: { taskId }, header: { 'Flow-Process-User': userHeader } },
    body,
  })
}

export async function retryTask(taskId: string, body?: RetryTaskRequest, userHeader?: string) {
  return await flowClient.POST('/tasks/{taskId}/retry', {
    params: { path: { taskId }, header: { 'Flow-Process-User': userHeader } },
    body,
  })
}

export async function getProcesses(query?: ProcessQuery, userHeader?: string) {
  const { data } = await flowClient.GET('/processes', {
    params: { query, header: { 'Flow-Process-User': userHeader } },
  })
  return data
}

export async function getProcessById(processId: string, userHeader?: string) {
  const { data } = await flowClient.GET('/processes/{processId}', {
    params: { path: { processId }, header: { 'Flow-Process-User': userHeader } },
  })
  return data
}
